import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CalendarClock, Sparkles, Repeat, Timer } from "lucide-react";
import StatusBadge from "@/components/app/StatusBadge";

const WINDOW_COLORS = {
  "12m": { border: "border-rose-300", bg: "bg-rose-50", text: "text-rose-800", dot: "bg-rose-500" },
  "24m": { border: "border-amber-300", bg: "bg-amber-50", text: "text-amber-800", dot: "bg-amber-500" },
  "36m": { border: "border-sky-300", bg: "bg-sky-50", text: "text-sky-800", dot: "bg-sky-500" },
  "later": { border: "border-emerald-300", bg: "bg-emerald-50", text: "text-emerald-800", dot: "bg-emerald-500" },
};

export default function RefreshEngine() {
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState("12m");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/refresh-engine/summary").then(({ data }) => setSummary(data)); }, []);
  useEffect(() => {
    setLoading(true);
    api.get("/refresh-engine/candidates", { params: { window: selected, limit: 200 } })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, [selected]);

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Intelligence · Planning</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Refresh Engine</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Proactive 12 / 24 / 36 month forecast. Blends age, warranty, condition and product intelligence into a single refresh score.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {(summary?.buckets || []).map((b) => {
          const c = WINDOW_COLORS[b.window] || WINDOW_COLORS.later;
          const active = selected === b.window;
          return (
            <button
              key={b.window}
              onClick={() => setSelected(b.window)}
              data-testid={`refresh-tile-${b.window}`}
              className={`text-left rounded-md border p-4 transition-all ${c.border} ${c.bg} ${c.text} ${active ? "ring-2 ring-slate-900 ring-offset-2" : "hover:brightness-95"}`}
            >
              <div className="flex items-center justify-between">
                <span className={`h-2 w-2 rounded-full ${c.dot}`} />
                <div className="text-[10px] font-semibold tracking-[0.08em] uppercase opacity-70">{b.window}</div>
              </div>
              <div className="mt-3 text-2xl font-heading font-bold tabular-nums">{b.count.toLocaleString()}</div>
              <div className="mt-0.5 text-xs">{b.label}</div>
              <div className="mt-2 text-[11px] opacity-80 tabular-nums">USD {Math.round(b.value).toLocaleString()}</div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-md p-4 lg:col-span-1">
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />
            Top scoring signals
          </div>
          <div className="mt-2 space-y-1.5">
            {(summary?.top_reasons || []).map((r) => (
              <div key={r.reason} className="flex items-center justify-between text-sm">
                <span className="text-slate-700">{r.reason}</span>
                <span className="tabular-nums font-medium text-slate-800">{r.count}</span>
              </div>
            ))}
            {(!summary?.top_reasons || summary.top_reasons.length === 0) && <div className="text-xs text-slate-500">No signals yet</div>}
          </div>
        </div>

        <div className="lg:col-span-2 bg-slate-900 text-slate-200 rounded-md p-5">
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-amber-400">How the engine scores</div>
          <div className="mt-1 font-heading text-lg text-white flex items-center gap-2"><Repeat className="h-4 w-4" strokeWidth={1.5} /> Deterministic refresh score</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 mt-3 text-xs text-slate-300 leading-relaxed">
            <div>• Age &gt; 4y adds 40 pts, &gt; 3y adds 30, &gt; 2y adds 20</div>
            <div>• Warranty expired adds 30 pts, ≤ 90d adds 20</div>
            <div>• Condition C/D adds 25, B adds 10</div>
            <div>• Past OEM refresh cycle adds 15, near EOL adds 20</div>
          </div>
          <div className="mt-3 text-xs text-slate-400">Score ≥ 60 → 12m · ≥ 40 → 24m · ≥ 20 → 36m · else later</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-800">Candidates — {WINDOW_LABEL(selected)}</div>
            <div className="text-xs text-slate-500">{items.length} devices · ranked by refresh score</div>
          </div>
          <CalendarClock className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5">Device</th>
              <th className="text-left px-4 py-2.5">Serial</th>
              <th className="text-left px-4 py-2.5">Age</th>
              <th className="text-left px-4 py-2.5">Condition</th>
              <th className="text-left px-4 py-2.5">Signals</th>
              <th className="text-right px-4 py-2.5">Score</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No candidates</td></tr>
            ) : items.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  <div className="font-medium text-slate-800">{a.brand} {a.model}</div>
                  <div className="text-xs text-slate-500 font-mono">{a.asset_tag}</div>
                </td>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{a.serial}</td>
                <td className="px-4 py-2.5 text-slate-700 text-xs">
                  {a.age_days != null ? (
                    <span className="inline-flex items-center gap-1"><Timer className="h-3 w-3 text-slate-400" />{Math.round(a.age_days / 30)} months</span>
                  ) : "—"}
                </td>
                <td className="px-4 py-2.5"><StatusBadge value={a.condition} /></td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {(a.refresh_reasons || []).slice(0, 3).map((r, i) => (
                      <span key={i} className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{r}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-slate-900">{a.refresh_score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WINDOW_LABEL(w) {
  if (w === "12m") return "Refresh in 12 months";
  if (w === "24m") return "Refresh in 24 months";
  if (w === "36m") return "Refresh in 36 months";
  return "Beyond 3 years";
}
